import { useState, useRef, useEffect, useCallback } from "react";

type UseCountdownTimerReturn = {
  seconds: number;
  isRunning: boolean;
  start: () => void;
  pause: () => void;
  reset: () => void;
  setTime: (seconds: number) => void;
};

export function useCountdownTimer(
  initialSeconds: number = 60
): UseCountdownTimerReturn {
  const [seconds, setSeconds] = useState(initialSeconds);
  const [isRunning, setIsRunning] = useState(false);
  const intervalRef = useRef<number | null>(null);

  const tick = useCallback(() => {
    setSeconds((prev) => {
      if (prev <= 1) {
        clearInterval(intervalRef.current!);
        intervalRef.current = null;
        setIsRunning(false);
        return 0;
      }
      return prev - 1;
    });
  }, []);

  const start = useCallback(() => {
    if (!isRunning && seconds > 0) {
      setIsRunning(true);
    }
  }, [isRunning, seconds]);

  const pause = useCallback(() => {
    setIsRunning(false);
  }, []);

  const reset = useCallback(() => {
    setIsRunning(false);
    setSeconds(initialSeconds);
  }, [initialSeconds]);

  const setTime = useCallback((newSeconds: number) => {
    setIsRunning(false);
    setSeconds(newSeconds);
  }, []);

  useEffect(() => {
    if (isRunning && intervalRef.current === null) {
      intervalRef.current = window.setInterval(tick, 1000);
    }

    if (!isRunning && intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    return () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isRunning, tick]);

  return { seconds, isRunning, start, pause, reset, setTime };
}
